import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { inject } from 'mobx-react';

import InputText from './InputText';
import { hoverDefault, lineThroughHover, toVw, toVwMobile, toVwTablet } from '../utils/styles';
import UploadScreen from './UploadScreen';
import { INPUT_HEIGHT } from '../constants';
import mq from '@/utils/media-queries';

const Wrapper = styled.div`
  position: relative;
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  color: ${props => props.theme.colorText};
  cursor: text;
  user-select: none;
  line-height: ${toVw(INPUT_HEIGHT)};

  ${mq.tablet(css`
    line-height: ${toVwTablet(INPUT_HEIGHT)};
  `)};

  ${mq.mobile(css`
    line-height: ${toVwMobile(INPUT_HEIGHT)};
  `)};
`;

const Input = styled(InputText)`
  width: 100%;
`;

const UploadLink = styled.span`
	display: inline-block;
	color: ${props => props.theme.colorAccent};
	cursor: pointer;
	white-space: nowrap;
	text-overflow: ellipsis;
	max-width: 100%;
	overflow: hidden;
	vertical-align: top;
	${hoverDefault};

	${props =>
    props.lineThrough &&
    css`
      ${lineThroughHover(props.theme.colorAccent)};

      &:hover {
        opacity: 1;
      }
    `};
	};
`;

const InputFile = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  color: transparent;
`;

const HiddenInputWrapper = styled.div`
  visibility: hidden;
  opacity: 0;

  input {
    position: absolute;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    height: 0;
    width: 0;
    -webkit-appearance: button;
    overflow: hidden;
  }
`;

class InputLinkOrFile extends React.Component {
  static propTypes = {
    nameInputText: PropTypes.string.isRequired,
    nameInputFile: PropTypes.string.isRequired,
    valueInputText: PropTypes.string.isRequired,
    valueInputFile: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    dataTest: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    _error: PropTypes.string,
    scrollControllerStore: PropTypes.object.isRequired,
  };

  state = {
    inputActive: false,
    uploadScreenShown: false,
    filename: '',
  };

  componentDidMount() {
    this.inputNode.addEventListener('blur', this.handleInputBlur);
  }

  componentWillUnmount() {
    this.inputNode.removeEventListener('blur', this.handleInputBlur);
  }

  handleInputFocus = () => {
    this.setState({
      inputActive: true,
    });
  };

  handlePlaceholderClick = () => {
    this.setState({
      inputActive: true,
    });
    this.inputNode.focus();
  };

  handleInputBlur = () => {
    this.setState({
      inputActive: false,
    });
  };

  showUploadScreen = e => {
    e.stopPropagation();
    this.props.scrollControllerStore.lock();
    this.setState({
      uploadScreenShown: true,
    });
  };

  hideUploadScreen = () => {
    this.props.scrollControllerStore.unlock();
    this.setState({
      uploadScreenShown: false,
    });
  };

  handleInputFileChange = e => {
    if (!e.target.value) {
      return;
    }

    const filename = e.target.files[0].name || e.target.files[0].fileName;

    this.uploadHiddenNode.appendChild(e.target.cloneNode());

    e.target.parentNode.removeChild(e.target);

    this.props.onChange(e);

    this.setState({
      filename,
    });
    this.props.setFieldValue(this.props.nameInputFile, e.currentTarget.files[0]);

    this.hideUploadScreen();
  };

  clearInputFile = () => {
    this.setState({
      filename: '',
    });
    this.uploadHiddenNode.innerHTML = '';
    this.props.setFieldValue(this.props.nameInputFile, '');
  };

  renderPlaceholder() {
    if (this.state.filename) {
      return (
        <Placeholder>
          <UploadLink lineThrough onClick={this.clearInputFile} title={this.state.filename}>
            {this.state.filename}
          </UploadLink>
        </Placeholder>
      );
    }

    if (this.state.inputActive || this.props.valueInputText) {
      return null;
    }

    return (
      <Placeholder onClick={this.handlePlaceholderClick}>
        {this.props.placeholder} / <UploadLink onClick={this.showUploadScreen}>загрузить</UploadLink>
      </Placeholder>
    );
  }

  render() {
    const { className, nameInputText, nameInputFile, valueInputText, onChange, dataTest, _error } = this.props;

    return (
      <Wrapper className={className} data-test={dataTest}>
        <Input
          name={nameInputText}
          inputRef={node => (this.inputNode = node)}
          value={valueInputText}
          onChange={onChange}
          onFocus={this.handleInputFocus}
          _error={_error}
        />
        {this.renderPlaceholder()}
        <UploadScreen onCloseClick={this.hideUploadScreen} shown={this.state.uploadScreenShown}>
          <InputFile name={nameInputFile} type="file" onChange={this.handleInputFileChange} />
        </UploadScreen>
        <HiddenInputWrapper innerRef={node => (this.uploadHiddenNode = node)} />
      </Wrapper>
    );
  }
}

export default inject(stores => ({
  scrollControllerStore: stores.store.scrollControllerStore,
}))(InputLinkOrFile);



// WEBPACK FOOTER //
// ./src/components/InputLinkOrFile.js